import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import GoogleSignIn from "../auth/GoogleSignIn.jsx";
import { FAQ_BLOCK } from "../data/faqs.js";

const apiBase = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
const interestEndpoint = apiBase ? `${apiBase}/api/interest` : "/api/interest";

export default function ComingSoon() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [showSignIn, setShowSignIn] = useState(false);
  const [signInMode, setSignInMode] = useState("admin");
  const [openFaq, setOpenFaq] = useState(null);
  const faqRef = useRef(null);

  const storeInterestLocally = (value) => {
    try {
      if (typeof window === "undefined") return false;
      const existing = JSON.parse(localStorage.getItem("offlineInterest") ?? "[]");
      existing.push({ email: value, storedAt: new Date().toISOString() });
      localStorage.setItem("offlineInterest", JSON.stringify(existing));
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setMessage("Please enter an email address.");
      setStatus("error");
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setMessage("Please enter a valid email address.");
      setStatus("error");
      return;
    }
    
    setStatus("loading");
    setMessage("");
    try {
      const response = await fetch(interestEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: trimmedEmail,
          source: "landing_page"
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Unable to save your interest right now.");
      }
      
      setStatus("success");
      setMessage("✓ Registered! You'll receive an email when we launch.");
      setEmail("");
    } catch (error) {
      console.error("Interest registration error:", error);
      // Fallback to localStorage only if API completely fails
      const saved = storeInterestLocally(trimmedEmail);
      if (saved) {
        setStatus("success");
        setMessage("⟳ Saved locally. We'll sync your interest when back online.");
        setEmail("");
      } else {
        setStatus("error");
        setMessage(error.message || "Something went wrong. Please try again later.");
      }
    }
  };

  const handleScrollToFAQ = () => {
    faqRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <main className="relative min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.08),transparent_60%)] text-white overflow-hidden bg-black">
        <div className="absolute top-4 right-4 z-20 flex gap-2">
          <button
            type="button"
            className="rounded-full border border-white/30 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:bg-white/10"
            onClick={() => {
              setSignInMode("user");
              setShowSignIn(true);
            }}
          >
            User login
          </button>
          <button
            type="button"
            className="rounded-full border border-white/30 px-4 py-2 text-xs uppercase tracking-[0.25em] hover:bg-white/10"
            onClick={() => {
              setSignInMode("admin");
              setShowSignIn(true);
            }}
          >
            Admin access
          </button>
        </div>
        <div className="absolute inset-0 grid-bg pointer-events-none" aria-hidden="true" />
        <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-6 text-center space-y-8">
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.35em] text-white/40">home ai</p>
            <h1 className="text-4xl md:text-6xl font-light">
              welcome <span className="shine-text font-semibold">home</span>
            </h1>
            <p className="text-sm text-white/60 max-w-lg mx-auto">
              We’re preparing a new era of AI-guided home selling and buying. Be the first to know when we launch.
            </p>
          </div>
          <div className="rounded-[32px] border border-white/10 bg-black/60 p-6 max-w-md w-full backdrop-blur-2xl shadow-[0_25px_80px_rgba(0,0,0,0.65)] space-y-4">
            <p className="text-xs uppercase tracking-[0.35em] text-white/60">Register interest</p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full rounded-full bg-black/60 border border-white/20 px-5 py-3.5 text-sm text-white placeholder-white/40 focus:border-white/50 focus:outline-none focus:ring-0"
                required
              />
              <button
                type="submit"
                className="w-full rounded-full bg-white text-black py-3.5 text-sm font-medium tracking-[0.2em] uppercase shadow-[0_12px_30px_rgba(255,255,255,0.2)] hover:bg-white/90 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={status === "loading"}
              >
                {status === "loading" ? "Sending…" : "Notify me"}
              </button>
              {message && (
                <p
                  className={`text-xs ${
                    status === "success" ? "text-emerald-200 uppercase tracking-[0.2em]" : "text-rose-300"
                  }`}
                >
                  {message}
                </p>
              )}
            </form>
          </div>
          <p className="text-[10px] uppercase tracking-[0.35em] text-white/30">Coming soon • home-ai.uk</p>
        </div>
        <div className="absolute bottom-8 inset-x-0 flex justify-center">
          <button
            type="button"
            onClick={handleScrollToFAQ}
            className="text-white/70 hover:text-white transition flex flex-col items-center gap-2"
          >
            <span className="text-xs tracking-[0.5em] uppercase">More</span>
            <span className="animate-bounce">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M6 10l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
        </div>
        <GoogleSignIn
          open={showSignIn}
          onClose={() => setShowSignIn(false)}
          allowedDomain={signInMode === "admin" ? "thebreakco.com" : undefined}
          showGoogle={signInMode === "admin"}
          title={signInMode === "admin" ? "Admin sign in" : "User sign in"}
          manualButtonLabel={signInMode === "admin" ? "Sign in" : "Sign in with email + password"}
          enableTestAccounts={false}
          onSignedIn={(session) => {
            const email = session?.email?.toLowerCase();
            const isAllowed = 
              email?.endsWith("@thebreakco.com") || 
              email === "info@home-ai.uk" ||
              email === "fishintheseasemail@gmail.com";
            console.log("[ComingSoon] onSignedIn - email:", email, "isAllowed:", isAllowed, "session:", session);
            if (signInMode === "admin" && !isAllowed) {
              console.log("[ComingSoon] Email not allowed, returning early");
              return;
            }
            setShowSignIn(false);
            // Redirect to appropriate dashboard based on roles
            if (session?.roles?.length) {
              console.log("[ComingSoon] Has roles, redirecting to /crm");
              navigate("/crm", { replace: true });
            } else {
              console.log("[ComingSoon] No roles, redirecting to /login");
              navigate("/login", { replace: true });
            }
          }}
        />
      </main>

      <section
        ref={faqRef}
        className="bg-black text-white border-t border-white/5 px-6 py-16 sm:py-20"
      >
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-[0.5em] text-white/40">Home AI — FAQs</p>
            <h2 className="text-3xl font-light">Frequently Asked Questions</h2>
            <p className="text-white/70 text-sm max-w-2xl mx-auto">
              Scroll through the essentials on valuations, launch timelines, developer briefs and buyer workflows.
            </p>
          </div>
          <div className="divide-y divide-white/10 rounded-3xl border border-white/10 bg-white/5 backdrop-blur">
            {FAQ_BLOCK.map((item, index) => {
              const isOpen = openFaq === index;
              return (
                <button
                  key={item.q}
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? null : index)}
                  className="w-full text-left px-6 py-5 flex flex-col gap-2 hover:bg-white/5 transition"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-base font-medium">{item.q}</span>
                    <span
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border border-white/30 text-xs transition ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </div>
                  <div
                    className={`text-sm text-white/70 leading-relaxed transition-[max-height,opacity] duration-300 ${
                      isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0 overflow-hidden"
                    }`}
                  >
                    {item.a}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
